import React, { useEffect, useState } from "react";
import { useSearchParams } from "react-router-dom";
import { Printer, Settings } from "lucide-react";
import { api } from "@/lib/api";
import { Button } from "@/components/ui/button";
import { SampleLabel, TAG_WIDTH_MM, TAG_HEIGHT_MM } from "@/pages/admin/SampleLabel";
import { hasAdminSession } from "@/lib/session";

// PrintTagsPage — a stand-alone printable route for the CD410 hang-tag printer.
// It opens in a new tab so browser Print produces just the tags (no admin chrome).
// Reads:
//   ?barcodes=A,B,C        → prints these specific pieces in that order (small lists)
//   ?product_id=UUID       → prints every piece for the design (helpful for bulk)
//   ?batch=sessionKey      → prints a large multi-product selection resolved by the
//                            Inventory "Print Selected Tags" flow. The barcode array
//                            is stashed in sessionStorage by BulkPrintDialog because
//                            hundreds of codes would otherwise blow past a safe URL
//                            length — this looks it up and fetches via POST instead.
// Every tag is exactly one 100 x 15 mm page (@page size below) so the roll printer
// advances one label per piece. Which fields print comes from Label Settings.
export default function PrintTagsPage() {
  const [params] = useSearchParams();
  const [pieces, setPieces] = useState([]);
  const [settings, setSettings] = useState(null);
  const [error, setError] = useState("");
  const [productMap, setProductMap] = useState({});

  useEffect(() => {
    // The Print Tags page opens in a NEW tab and doesn't share the SPA's
    // in-memory auth state — but the JWT cookie set at login is domain-scoped,
    // so axios (which sends credentials: true) will still be authenticated
    // as long as the user is logged in in this browser. If not, direct to login.
    if (!hasAdminSession()) {
      window.location.href = "/admin/login?next=/admin/print-tags";
      return;
    }
    let cancelled = false;
    (async () => {
      try {
        // 1. Load label config so we know which fields to show and the shop name.
        const { data: s } = await api.get("/admin/label-settings");
        if (!cancelled) setSettings(s);

        // 2. Load the pieces requested via URL. `barcodes` / `batch` take precedence.
        const barcodes = params.get("barcodes");
        const batchKey = params.get("batch");
        const productId = params.get("product_id");
        let pcs = [];
        if (barcodes) {
          const { data } = await api.get("/inventory/pieces", { params: { barcodes } });
          pcs = data.pieces || [];
        } else if (batchKey) {
          const stored = sessionStorage.getItem(batchKey);
          const codes = stored ? JSON.parse(stored) : [];
          if (!codes.length) { setError("This print batch has expired. Please re-select the products and print again from Inventory."); return; }
          const { data } = await api.post("/inventory/pieces/lookup", { barcodes: codes });
          pcs = data.pieces || [];
        } else if (productId) {
          const { data } = await api.get(`/inventory/products/${productId}/pieces`);
          pcs = data.pieces || [];
        } else {
          setError("Missing barcodes, batch or product_id in URL");
          return;
        }
        if (cancelled) return;
        setPieces(pcs);

        // 3. Fetch the parent product for each unique product_id — the selling
        //    price and the design name live on the product, not the piece.
        const uniq = Array.from(new Set(pcs.map((p) => p.product_id).filter(Boolean)));
        const products = await Promise.all(uniq.map((pid) => api.get(`/products/${pid}`).then((r) => r.data).catch(() => null)));
        if (cancelled) return;
        const map = {};
        products.forEach((p) => { if (p?.id) map[p.id] = p; });
        setProductMap(map);
      } catch (e) {
        setError(e?.response?.data?.detail || e?.message || "Failed to load tags");
      }
    })();
    return () => { cancelled = true; };
  }, [params]);

  const doPrint = () => window.print();

  if (error) {
    return (
      <div className="p-8 text-center text-destructive">
        {error}
      </div>
    );
  }

  if (!settings) {
    return <div className="p-8 text-center text-muted-foreground">Loading label settings…</div>;
  }

  return (
    <div className="min-h-screen bg-[#F7F0E6] p-4 print:p-0 print:bg-white">
      {/* Top bar — hidden from print output via @media rules below */}
      <div className="print:hidden max-w-4xl mx-auto mb-4 flex items-center justify-between flex-wrap gap-3 bg-white border border-border rounded-lg p-3 shadow-sm">
        <div className="text-sm">
          <span className="font-medium">Print Tags ({pieces.length})</span>
          <span className="text-muted-foreground ml-2">CD410 hang tag · {TAG_WIDTH_MM} × {TAG_HEIGHT_MM} mm · one tag per label</span>
        </div>
        <div className="flex items-center gap-2 flex-wrap">
          <Button data-testid="print-settings-link" size="sm" variant="outline" className="gap-1" onClick={() => window.open("/admin/rates", "_blank")}>
            <Settings className="h-3.5 w-3.5" /> Label Settings
          </Button>
          <Button data-testid="print-now-button" size="sm" onClick={doPrint} className="gap-1"><Printer className="h-3.5 w-3.5" /> Print</Button>
        </div>
      </div>

      {/* One tag per 100 x 15 mm page. On screen they stack with a gap and a soft
          shadow so each label is distinguishable against the beige backdrop; in
          print the gap and shadow vanish so the only ink is the tag content. */}
      <div className="mx-auto flex flex-col items-start print:items-stretch" data-testid="print-tags-sheet">
        {pieces.map((piece, idx) => {
          const product = productMap[piece.product_id];
          const gross = Number(piece.gross_weight_g) || 0;
          const net = Number(piece.net_weight_g) || 0;
          const stone = piece.stone_weight_g != null && piece.stone_weight_g !== ""
            ? Number(piece.stone_weight_g)
            : Math.max(gross - net, 0);
          // Diamond weight: piece-level override first, else sum the design's
          // diamonds[].carat. Quality only lives on the design — join all of them.
          const productDiamonds = Array.isArray(product?.diamonds) ? product.diamonds : [];
          const diamondCarat = piece.diamond_carat_total != null && piece.diamond_carat_total !== ""
            ? Number(piece.diamond_carat_total)
            : productDiamonds.reduce((sum, d) => sum + (Number(d?.carat) || 0), 0);
          const diamondQuality = productDiamonds
            .map((d) => (d?.quality || "").trim())
            .filter(Boolean)
            .join(", ");
          const demo = {
            barcode: piece.barcode,
            product_sku: piece.product_sku,
            product_name: product?.name || piece.product_name || "",
            purity: piece.purity,
            gross_weight_g: gross,
            net_weight_g: net,
            stone_weight_g: stone,
            diamond_carat: diamondCarat,
            diamond_quality: diamondQuality || null,
            price: product?.price ?? null,
            // Real pieces: pass null (not undefined) when empty so the tag omits the
            // line instead of falling back to the Label Settings sample placeholder.
            huid: piece.huid || null,
          };
          const isLast = idx === pieces.length - 1;
          return (
            <div
              key={piece.id}
              className={`mb-3 shadow-md print:shadow-none print:mb-0 ${!isLast ? "print-tag-break" : ""}`}
              data-testid="print-tag-card"
            >
              <SampleLabel settings={settings} demo={demo} />
            </div>
          );
        })}
        {pieces.length === 0 && (
          <div className="p-8 text-center text-muted-foreground">No pieces to print.</div>
        )}
      </div>

      {/* Print-only CSS. The page is the exact label size with zero margin so the
          CD410 advances one 100 x 15 mm label per tag, and nothing — no borders,
          shadows or background tints — prints except the tag content itself. */}
      <style>{`
        @media print {
          @page { size: ${TAG_WIDTH_MM}mm ${TAG_HEIGHT_MM}mm; margin: 0; }
          html, body { margin: 0 !important; padding: 0 !important; background: #fff !important; }
          .print\\:hidden { display: none !important; }
          .print\\:p-0 { padding: 0 !important; }
          .print\\:bg-white { background: #fff !important; }
          .print\\:shadow-none { box-shadow: none !important; }
          .print\\:mb-0 { margin-bottom: 0 !important; }
          [data-testid="print-tag-card"] { border: 0 !important; outline: 0 !important; box-shadow: none !important; break-inside: avoid; page-break-inside: avoid; }
          .print-tag-break { break-after: page; page-break-after: always; }
        }
      `}</style>
    </div>
  );
}
